/**
 * Instagram feed tile — used by sections/retrouve-nous.liquid (it was written
 * for the bento layout, which was dropped on 2026-08-27; the tile itself
 * moved into the new section unchanged).
 *
 * There is no anonymous Instagram API: reading a profile's posts requires an
 * access token, and a token cannot live in theme code because the theme is
 * public. So this element never talks to Instagram directly. It fetches a JSON
 * endpoint (a relay service, or our own serverless function) that holds the
 * token server-side and returns the latest posts.
 *
 * The element is deliberately tolerant about the JSON shape, so the same code
 * works whether the endpoint mirrors the Meta Graph API or a relay's own
 * format. If anything fails, the markup rendered by Liquid — the manually
 * picked fallback images — is simply left untouched.
 */

const CACHE_PREFIX = 'yumbei:instagram:';
const CACHE_TTL = 15 * 60 * 1000; // 15 min: the tiles are decorative, not live data.

/** Pull the post list out of the payload, whatever key it hides behind. */
function extractPosts(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  // `data` is the Graph API shape; `media` and `posts` are used by relay services.
  const candidates = [payload.data, payload.media, payload.posts];
  return candidates.find(Array.isArray) || [];
}

/**
 * Reduce one post to what a tile needs.
 * Videos and reels expose an mp4 in `media_url`, so their poster frame has to
 * be read from `thumbnail_url` instead. A tile is ~380px wide, so a
 * pre-resized variant is preferred over the full-resolution original.
 */
function normalizePost(post) {
  const isVideo = String(post.media_type || post.mediaType || '').toUpperCase() === 'VIDEO';
  const thumbnail = post.thumbnail_url || post.thumbnailUrl;
  const resized = post.sizes?.medium?.mediaUrl || post.sizes?.large?.mediaUrl;
  const original = post.media_url || post.mediaUrl;

  return {
    image: (isVideo ? thumbnail : resized || original) || thumbnail || original,
    permalink: post.permalink || post.url,
    // `altText` is a real description when the endpoint provides one; the
    // caption is only a stand-in for it.
    altText: (post.altText || '').trim(),
    caption: (post.prunedCaption || post.caption || '').trim(),
  };
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;

    const { at, posts } = JSON.parse(raw);
    return Date.now() - at < CACHE_TTL ? posts : null;
  } catch {
    // Private browsing, quota, corrupted entry: the cache is optional.
    return null;
  }
}

function writeCache(key, posts) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ at: Date.now(), posts }));
  } catch {
    /* Ignore: caching is a nicety, not a requirement. */
  }
}

class InstagramFeed extends HTMLElement {
  connectedCallback() {
    this.endpoint = this.dataset.endpoint;
    if (!this.endpoint) return; // No endpoint configured: keep the fallback images.

    this.tiles = Array.from(this.querySelectorAll('[data-instagram-tile]'));
    if (this.tiles.length === 0) return;

    this.load();
  }

  async load() {
    const cached = readCache(this.endpoint);
    if (cached) {
      this.render(cached);
      return;
    }

    try {
      const response = await fetch(this.endpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Instagram feed responded ${response.status}`);

      const posts = extractPosts(await response.json())
        .slice(0, this.tiles.length)
        .map(normalizePost)
        .filter((post) => post.image && post.permalink);

      if (posts.length === 0) throw new Error('Instagram feed returned no usable post');

      writeCache(this.endpoint, posts);
      this.render(posts);
    } catch (error) {
      // Leaving the fallback images in place is the correct degraded state,
      // so this is logged rather than surfaced to the visitor.
      console.warn('[yumbei] Instagram feed unavailable, using fallback images.', error);
    }
  }

  render(posts) {
    posts.forEach((post, index) => {
      const tile = this.tiles[index];
      if (!tile) return;

      // The tile's one and only <img> — everything else in it is inline SVG.
      // Hooked on the element rather than on a class name (it used to be
      // `.bento__insta-image`): class names belong to whichever section hosts
      // the tile, and they did change when the bento was dropped.
      const image = tile.querySelector('img');
      if (image) {
        // srcset/sizes come from the Liquid fallback and would override src.
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = post.image;
        image.loading = 'lazy';
        const description = post.altText || post.caption.slice(0, 120);
        image.alt = description
          ? `Instagram Yumbei : ${description}`
          : 'Publication Instagram de Yumbei';
      }

      // Deep-link to the post itself rather than the profile once we know it.
      tile.href = post.permalink;
      tile.dataset.instagramLoaded = 'true';
    });

    this.dataset.state = 'loaded';
  }
}

if (!customElements.get('instagram-feed')) {
  customElements.define('instagram-feed', InstagramFeed);
}
